import { useMemo } from "react"
import {
  Chart,
  TableVisualization,
  ComposedChart,
} from "~/ui-rtk/components/ui/charts"
import { DateRangePicker, MultiSelect } from "~/ui-rtk/components/ui/controls"
import { useMinMaxDateRange } from "~/ui-rtk/hooks/date-range"
import { usePeriod, DATE_FORMAT, isValidDate } from "~/ui-rtk/hooks/period"
import useDate from "~/ui-rtk/hooks/date"
import {
  VISUALIZATION_TYPE,
  VISUALIZATIONS_MAP,
} from "~/ui-rtk/constants/charts"
import { brandRoutes } from "~/ui-rtk/constants/routes"
import Breadcrumbs from "~/ui-rtk/components/layout/Breadcrumbs"
import useUrlColumnKeys from "~/ui-rtk/hooks/url-column-keys"
import type { TMultiSelectOption } from "~/ui-rtk/components/ui/controls/MultiSelect"
import type { TableVisualizationColumn } from "~/ui-rtk/constants/table-visualization-types"
import TableFiltersBar from "~/ui-rtk/components/ui/controls/FiltersBar/TableFiltersBar"
import useSources from "~/ui-rtk/hooks/sources"
import useSelectFilterOptions from "./hooks/useSelectFilterOptions"

const dayjs = useDate()
const CHART_WIDGET_ID = "company_web_analytics_landing_page_report_chart"
const TABLE_WIDGET_ID = "company_web_analytics_landing_page_report_table"
const URL_PREFIX = "landing_page_report"

export default function LandingPageReportPage() {
  const { dateRange, compareRange } = useMinMaxDateRange()
  const { granularity, setPeriod, setComparePeriod } = usePeriod()
  const { sources } = useSources()
  const { getSelectOptions, isLoading: isLoadingFilterOptions } =
    useSelectFilterOptions()

  const chartProps = useMemo(
    () => VISUALIZATIONS_MAP[CHART_WIDGET_ID]?.props,
    [],
  )
  const tableProps = useMemo(
    () => VISUALIZATIONS_MAP[TABLE_WIDGET_ID]?.props,
    [],
  )

  const defaultColumnKeys = useMemo(
    () =>
      tableProps?.columnConfig
        .filter(
          (column: TableVisualizationColumn & { disabled?: boolean }) =>
            !column.disabled,
        )
        .map((column: TableVisualizationColumn) => column.key) || [],
    [tableProps],
  )

  const { columnKeys, setColumnKeys } = useUrlColumnKeys(
    URL_PREFIX,
    defaultColumnKeys,
  )

  const columnOptions = useMemo(() => {
    const options = tableProps?.columnConfig.map(
      (column: TableVisualizationColumn) =>
        ({
          value: column.key,
          label: column.header,
        }) as TMultiSelectOption,
    )
    return options || []
  }, [tableProps])

  const columnConfigFiltered = useMemo(
    () =>
      (tableProps?.columnConfig ?? [])
        .filter(({ key }: { key: string }) =>
          (columnKeys as string[])?.includes(key),
        )
        .map(
          ({
            _disabled,
            ...rest
          }: TableVisualizationColumn & { _disabled?: boolean }) => rest,
        ),
    [tableProps?.columnConfig, columnKeys],
  )

  const changePeriod = ({
    start,
    end,
    compareStart,
    compareEnd,
  }: {
    start: Date
    end: Date
    compareStart?: Date | null
    compareEnd?: Date | null
  }) => {
    void setPeriod(
      dayjs(start).format(DATE_FORMAT),
      dayjs(end).format(DATE_FORMAT),
    )

    if (compareStart && compareEnd) {
      setComparePeriod(
        dayjs(compareStart).format(DATE_FORMAT),
        dayjs(compareEnd).format(DATE_FORMAT),
      )
    } else {
      setComparePeriod()
    }
  }

  return (
    <div className="px-4">
      <div className="flex items-center justify-between">
        <Breadcrumbs
          items={[
            {
              name: "Web Merchandising",
              slug: brandRoutes.WEB_MERCHANDISING.ROOT,
            },
            {
              name: "Landing Page Report",
              slug: brandRoutes.WEB_MERCHANDISING.LANDING_PAGE_REPORT,
            },
          ]}
        />
      </div>

      <div className="flex justify-end items-stretch gap-system-sm mt-system-lg mb-system-lg">
        {dateRange?.[0] &&
          dateRange?.[1] &&
          isValidDate(dateRange[0]) &&
          isValidDate(dateRange[1]) && (
            <DateRangePicker
              start={dateRange[0]}
              end={dateRange[1]}
              compareStart={
                compareRange?.[0] && isValidDate(compareRange[0])
                  ? compareRange[0]
                  : undefined
              }
              compareEnd={
                compareRange?.[1] && isValidDate(compareRange[1])
                  ? compareRange[1]
                  : undefined
              }
              comparisonEnabled={true}
              onChange={changePeriod}
            />
          )}
      </div>

      {dateRange?.[0] &&
        dateRange?.[1] &&
        isValidDate(dateRange[0]) &&
        isValidDate(dateRange[1]) && (
          <div className="">
            <Chart
              {...chartProps}
              chartId={CHART_WIDGET_ID}
              urlParamsPrefix={`${URL_PREFIX}_chart`}
              className="h-[600px]"
              widgetType={VISUALIZATION_TYPE.ComposedChart}
              component={ComposedChart}
              dateRange={dateRange}
              compareRange={compareRange ?? undefined}
              comparisonEnabled={true}
              granularity={granularity}
              sources={sources}
              hideAggSelector={true}
            />
          </div>
        )}

      <div className="flex justify-end items-stretch gap-system-sm mt-3 mb-3">
        <MultiSelect
          label="Columns"
          options={columnOptions as TMultiSelectOption[]}
          value={columnOptions.filter((option: TMultiSelectOption) =>
            (columnKeys as string[]).includes(option.value),
          )}
          onChange={(options: TMultiSelectOption[]) =>
            setColumnKeys(options.map(({ value }) => value))
          }
        />
        <TableFiltersBar
          id={URL_PREFIX}
          columnConfig={columnConfigFiltered}
          additionalFilters={tableProps?.additionalFilters}
          getSelectOptions={getSelectOptions}
          isLoadingOptions={isLoadingFilterOptions}
        />
      </div>

      <div>
        <Chart
          {...tableProps}
          chartId={TABLE_WIDGET_ID}
          urlParamsPrefix={URL_PREFIX}
          className=""
          widgetType={VISUALIZATION_TYPE.TableVisualization}
          component={TableVisualization}
          dateRange={dateRange}
          compareRange={compareRange ?? undefined}
          comparisonEnabled={true}
          compareUniqueKey="company_web_analytics_landing_page_report.handle"
          granularity={granularity}
          sources={sources}
          hideAggSelector={true}
          columnConfig={columnConfigFiltered}
          enableCSVExport={true}
          csvExportFilename="landing_page_report"
          defaultSorting={[
            {
              id: "company_web_analytics_landing_page_report.sessions",
              desc: true,
            },
          ]}
        />
      </div>
    </div>
  )
}
